import React, { useMemo, useState } from 'react';
import { Modal, Input, Button, Alert } from 'antd';

// Detect and build embed HTML for common video URLs (YouTube, Vimeo, direct files)
const buildEmbed = (rawUrl) => {
  if (!rawUrl) return { type: 'none', html: '' };
  const url = rawUrl.trim();

  // YouTube patterns
  const ytMatchShort = url.match(/https?:\/\/youtu\.be\/([\w-]{6,})/i);
  const ytMatchWatch = url.match(/https?:\/\/www\.youtube\.com\/watch\?([^#]+)/i);
  const ytMatchEmbed = url.match(/https?:\/\/www\.youtube\.com\/embed\/([\w-]{6,})/i);
  let ytId = null;
  if (ytMatchShort) ytId = ytMatchShort[1];
  if (!ytId && ytMatchEmbed) ytId = ytMatchEmbed[1];
  if (!ytId && ytMatchWatch) {
    const params = new URLSearchParams(ytMatchWatch[1]);
    ytId = params.get('v');
  }
  if (ytId) {
    const src = `https://www.youtube.com/embed/${ytId}`;
    const html = `<div class="rte-video" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 6px; margin: 10px 0;">
  <iframe src="${src}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"></iframe>
</div>`;
    return { type: 'youtube', html, previewSrc: src };
  }

  // Vimeo
  const vimeoMatch = url.match(/https?:\/\/vimeo\.com\/(\d+)/i);
  if (vimeoMatch) {
    const id = vimeoMatch[1];
    const src = `https://player.vimeo.com/video/${id}`;
    const html = `<div class="rte-video" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 6px; margin: 10px 0;">
  <iframe src="${src}" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"></iframe>
</div>`;
    return { type: 'vimeo', html, previewSrc: src };
  }

  // Direct video files
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
    const html = `<video controls style="max-width: 100%; border-radius: 6px; margin: 10px 0;">
  <source src="${url}" />
  Trình duyệt của bạn không hỗ trợ video.
</video>`;
    return { type: 'file', html, previewSrc: url };
  }

  return { type: 'unknown', html: '' };
};

const VideoInsertModal = ({ visible, onCancel, onInsert }) => {
  const [url, setUrl] = useState('');
  const embed = useMemo(() => buildEmbed(url), [url]);

  const handleInsert = () => {
    if (!embed.html) return;
    onInsert(embed.html);
    setUrl('');
  };

  return (
    <Modal
      title="Chèn video từ đường dẫn"
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Input
        placeholder="Dán đường dẫn YouTube, Vimeo hoặc file .mp4/.webm/.ogg"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onPressEnter={handleInsert}
        style={{ marginBottom: 12 }}
        allowClear
      />

      {url && embed.type === 'unknown' && (
        <Alert
          type="warning"
          showIcon
          message="Đường dẫn không hợp lệ"
          description="Hỗ trợ: YouTube, Vimeo hoặc liên kết trực tiếp tới file .mp4/.webm/.ogg"
          style={{ marginBottom: 12 }}
        />
      )}

      {embed.previewSrc && embed.type !== 'file' && (
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginBottom: 12 }}>
          <iframe
            src={embed.previewSrc}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Xem trước video"
          />
        </div>
      )}

      {embed.type === 'file' && (
        <video
          controls
          style={{ width: '100%', borderRadius: 6, marginBottom: 12 }}
          src={embed.previewSrc}
        />
      )}

      <Button
        type="primary"
        block
        disabled={!embed.html}
        onClick={handleInsert}
      >
        Chèn video
      </Button>
    </Modal>
  );
};

export default VideoInsertModal;
